import React from "react";
import { Hash } from "react-feather";
import { useAtomValue } from "jotai";
import { appNoteStore, pinnedPositions } from "../../model/services";
import { SearchQuerySetter } from "../../search/SearchQuery";
import { countHashtag } from "../countUtils";
import { updateSearchQuery } from "../../search/useSearchQuery";
import { getUserId, userSettingsAtom } from "../../model/atoms";
import { upsertUserSettings } from "../../model/userSettings";
import { trackEvent } from "../../analytics/analyticsHandlers";
import { addToast } from "../../components/Toast";
import { PinnedHashtag } from "../../../shared/types";
import { renameHashtag } from "../../model/store/utils";
import { fixPublicUrlInNativeApp } from "../../editorPage/utils/fixPublicUrlInNativeApp";
import { ApiClient } from "../../api/client";
import ElementBrowser, { SidebarElement } from "./ElementBrowser";

export const HashtagList: React.FC<{ setSearch: SearchQuerySetter }> = ({ setSearch }) => {
  const { pinnedHashtags, publicHashtags } = useAtomValue(userSettingsAtom);

  const elements: SidebarElement[] = countHashtag(appNoteStore).map(({ id, count, content }) => ({
    id,
    count,
    content: content.slice(1), // remove the leading #
    setSearch: () => setSearch({ hashtagsList: [id] }),
    rename: (newName: string) => {
      renameHashtag(content, "#" + newName);
      updateSearchQuery({ hashtagsList: ["#" + newName] });
    },
    isPublic: !!(publicHashtags || []).find((h) => h.toLowerCase() === content.toLowerCase()),
    isPinned: !!(pinnedHashtags || []).find((h) => h.id === id),
    setIsPublic: async (state: boolean) => {
      const userId = getUserId();
      if (!userId) {
        return;
      }
      const isPublic = !!publicHashtags.find((h) => h.toLowerCase() === content.toLowerCase());
      if (state && !isPublic) {
        const response = await ApiClient().users.updateNotesVisibilityByTag(userId, content, true);
        if (!response.updated) {
          return;
        }

        upsertUserSettings({ publicHashtags: [...publicHashtags, content] });
        addToast("Hashtag is now public. Link copied to clipboard.");

        const publicTagUrl = fixPublicUrlInNativeApp(
          window.location.protocol + "//" + window.location.host + `/sharedBy/${userId}/tag/${content.slice(1)}`,
        );

        navigator.clipboard.writeText(publicTagUrl);
        trackEvent("hashtag_public", content);
      } else if (!state && isPublic) {
        const response = await ApiClient().users.updateNotesVisibilityByTag(userId, content, false);
        if (!response.updated) {
          return;
        }
        upsertUserSettings({ publicHashtags: publicHashtags.filter((h) => h.toLowerCase() !== content.toLowerCase()) });
        addToast("Hashtag is now private");
        trackEvent("hashtag_private", content);
      }
    },
    setIsPinned: (newVal: boolean) => {
      const isPinned = !!pinnedHashtags.find((h) => h.id === id);
      if (newVal && !isPinned) {
        const newPinned: PinnedHashtag = { id, positionInPinned: pinnedPositions.generateFirst()[0], type: "hashtag" };
        upsertUserSettings({ pinnedHashtags: [newPinned, ...pinnedHashtags] });
        addToast("Hashtag pinned");
        trackEvent("hashtag_pinned", id);
      } else if (!newVal && isPinned) {
        upsertUserSettings({ pinnedHashtags: pinnedHashtags.filter((h) => h.id !== id) });
        addToast("Hashtag unpinned");
        trackEvent("hashtag_unpinned", id);
      }
    },
  }));

  return (
    <ElementBrowser
      header="Hashtags"
      type="hashtag"
      Icon={Hash}
      elements={elements}
      localStorageKey={"hashtag_sort"}
      placeholderText={"Type # to add your first hashtag"}
    />
  );
};
